/**
 * useInventoryData Hook
 * Shared hook for fetching inventory data across all types
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../supabaseClient';
import { getInventoryConfig } from './inventoryConfig';

/**
 * Hook for fetching inventory data
 * @param {string} inventoryType - Type of inventory (products, bottles, caps, etc.)
 * @param {string} companyId - Company ID
 * @param {object} options - Additional options (locationId, includeTransactions, etc.)
 * @returns {object} Inventory data and loading state
 */
export const useInventoryData = (inventoryType, companyId, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [aggregatedData, setAggregatedData] = useState([]);
  
  const config = getInventoryConfig(inventoryType);
  
  /**
   * Fetch inventory items with lot/batch details
   */
  const fetchInventoryItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query for inventory items
      // Select only fields that exist in the item tables
      // Note: reorder_point is in the aggregate inventory table, not the item table
      const itemFields = config.supportsVariants
        ? 'id, name, sku, description'
        : config.hasUnitOfMeasure
          ? 'id, name, sku, primary_unit_type, primary_unit_quantity, primary_unit_measure'
          : 'id, name, sku';
      
      // Build the select string — include product_variants join for products
      const selectParts = [
        '*',
        `${config.tableName} ( ${itemFields} )`,
        'locations ( id, name )'
      ];
      if (config.supportsVariants) {
        selectParts.push('product_variants ( id, name )');
      }

      let query = supabase
        .from(config.itemsTable)
        .select(selectParts.join(', '))
        .eq('company_id', companyId);
      
      // Apply location filter if specified
      if (options.locationId && options.locationId !== 'all') {
        query = query.eq('location_id', options.locationId);
      }
      
      // Filter by quantity - only show items with stock
      if (config.hasUnitConfig) {
        query = query.gt('total_base_units', 0);
      } else {
        query = query.gt(config.quantityField || 'current_quantity', 0);
      }
      
      // Fetch data
      const { data, error: fetchError } = await query.order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Fetch vendor/invoice information for each item
      const itemsWithVendor = await Promise.all(
        (data || []).map(async (item) => {
          if (item.source_transaction_id) {
            const { data: transaction } = await supabase
              .from(config.transactionsTable)
              .select(`
                *,
                suppliers (
                  id,
                  name
                ),
                from_location:from_location_id (
                  id,
                  name
                ),
                to_location:to_location_id (
                  id,
                  name
                )
              `)
              .eq('id', item.source_transaction_id)
              .single();
            
            if (transaction) {
              return {
                ...item,
                source_transaction: {
                  ...transaction,
                  supplier: transaction.suppliers,
                  from_location: transaction.from_location,
                  to_location: transaction.to_location
                },
                vendor_info: {
                  supplier_name: transaction.suppliers?.name,
                  supplier_id: transaction.supplier_id,
                  po_number: transaction.po_number,
                  po_date: transaction.po_date,
                  invoice_number: transaction.invoice_number,
                  invoice_date: transaction.invoice_date,
                  unit_cost: transaction.unit_cost,
                  from_location_name: transaction.from_location?.name,
                  to_location_name: transaction.to_location?.name
                },
                // Document paths for easy access (transaction-level docs)
                invoice_file_path: transaction.invoice_file_path || item.invoice_file_path,
                po_file_path: transaction.po_file_path || item.po_file_path,
                coa_file_path: transaction.coa_file_path || item.coa_file_path,
                msds_file_path: transaction.msds_file_path || item.msds_file_path,
                sds_file_path: transaction.sds_file_path || item.sds_file_path,
                bol_file_path: transaction.bol_file_path || item.bol_file_path,
                // production_doc only exists on items table, preserve item-level value
                production_doc_file_path: item.production_doc_file_path
              };
            }
          }
          return item;
        })
      );
      
      setInventoryData(itemsWithVendor);
      
      // Calculate aggregated data (sum by item and location)
      const aggregated = calculateAggregatedData(itemsWithVendor, config);
      
      // Fetch reorder points from aggregate inventory table
      const aggregatedWithReorder = await fetchReorderPoints(aggregated, config, companyId);
      setAggregatedData(aggregatedWithReorder);
      
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inventoryType, companyId, options.locationId]);
  
  /**
   * Fetch reorder points from aggregate inventory table
   */
  const fetchReorderPoints = async (aggregatedData, config, companyId) => {
    try {
      // Get all unique item_id and location_id combinations
      const keys = aggregatedData.map(item => ({
        item_id: item.item_id,
        location_id: item.location_id
      }));
      
      if (keys.length === 0) return aggregatedData;
      
      // Fetch reorder points from aggregate inventory table
      const { data: reorderData, error } = await supabase
        .from(config.inventoryTable)
        .select('*')
        .eq('company_id', companyId);
      
      if (error) {
        console.error('Error fetching reorder points:', error);
        return aggregatedData;
      }
      
      // Map reorder settings to aggregated data
      const reorderMap = {};
      (reorderData || []).forEach(item => {
        // Products include variant_id in the key
        const key = config.supportsVariants
          ? `${item[config.itemIdField]}_${item.variant_id || 'null'}_${item.location_id}`
          : `${item[config.itemIdField]}_${item.location_id}`;
        // Chemicals store reorder thresholds in unit-based columns
        if (config.hasUnitOfMeasure) {
          reorderMap[key] = {
            reorder_point:       item.reorder_point_units || 0,
            reorder_point_total: item.reorder_point_total || 0,
            reorder_quantity:    item.reorder_quantity_units || 0
          };
        } else {
          reorderMap[key] = {
            reorder_point:    item.reorder_point || 0,
            reorder_quantity: item.reorder_quantity || 0
          };
        }
      });

      // Add reorder settings to aggregated data
      const getReorderKey = (item) => config.supportsVariants
        ? `${item.item_id}_${item.variant_id || 'null'}_${item.location_id}`
        : `${item.item_id}_${item.location_id}`;

      return aggregatedData.map(item => ({
        ...item,
        reorder_point:    reorderMap[getReorderKey(item)]?.reorder_point    || 0,
        reorder_quantity: reorderMap[getReorderKey(item)]?.reorder_quantity || 0,
        ...(config.hasUnitOfMeasure && {
          reorder_point_total: reorderMap[getReorderKey(item)]?.reorder_point_total || 0
        })
      }));
    } catch (err) {
      console.error('Error fetching reorder points:', err);
      return aggregatedData;
    }
  };
  
  /**
   * Calculate aggregated inventory data
   */
  const calculateAggregatedData = (items, config) => {
    const aggregated = {};
    
    items.forEach(item => {
      const itemId = item[config.itemIdField];
      const locationId = item.location_id;
      // Products aggregate by variant too (same product, different variant = separate rows)
      const variantId = config.supportsVariants ? (item.variant_id || 'null') : null;
      const key = config.supportsVariants
        ? `${itemId}_${variantId}_${locationId}`
        : `${itemId}_${locationId}`;

      if (!aggregated[key]) {
        const itemMeta = item[config.tableName] || {};
        aggregated[key] = {
          item_id:       itemId,
          company_id:    item.company_id,
          location_id:   locationId,
          item_name:     itemMeta.name || 'Unknown',
          item_sku:      itemMeta.sku,
          location_name: item.locations?.name || 'Unknown',
          reorder_point: 0,
          total_quantity: 0,
          lot_count: 0,
          lots: []
        };

        // For products with multi-unit configuration (pallets/cases/units)
        if (config.hasUnitConfig) {
          aggregated[key].total_pallets = 0;
          aggregated[key].total_cases   = 0;
          aggregated[key].total_units   = 0;
          aggregated[key].total_base_units = 0;
          // Capture unit config from first lot
          aggregated[key].units_per_case   = item.units_per_case || 1;
          aggregated[key].cases_per_pallet = item.cases_per_pallet || 1;
          aggregated[key].enable_cases     = item.enable_cases ?? true;
          aggregated[key].enable_pallets   = item.enable_pallets ?? false;
        }

        // For products with variant support
        if (config.supportsVariants) {
          aggregated[key].variant_id   = item.variant_id || null;
          aggregated[key].variant_name = item.product_variants?.name || item.variant_name || null;
        }

        // For chemicals: capture unit label info from first lot
        if (config.hasUnitOfMeasure) {
          aggregated[key].total_base_quantity = 0;
          aggregated[key].unit_type        = itemMeta.primary_unit_type     || 'units';
          aggregated[key].unit_measure     = itemMeta.primary_unit_measure   || '';
          aggregated[key].unit_qty_per_unit = itemMeta.primary_unit_quantity || null;
        }
      }

      // Accumulate quantity
      if (config.hasUnitConfig) {
        aggregated[key].total_pallets    += item.pallet_quantity || 0;
        aggregated[key].total_cases      += item.case_quantity   || 0;
        aggregated[key].total_units      += item.unit_quantity   || 0;
        aggregated[key].total_base_units += item.total_base_units || 0;
        aggregated[key].total_quantity   += item.total_base_units || 0;
      } else if (config.hasUnitOfMeasure) {
        // Chemicals: total_quantity = sum of unit quantities (drums), total_base_quantity = sum of total weight/volume
        aggregated[key].total_quantity      += item.current_unit_quantity  || 0;
        aggregated[key].total_base_quantity += item.current_total_quantity || 0;
      } else {
        aggregated[key].total_quantity += item.current_quantity || 0;
      }

      aggregated[key].lot_count += 1;
      // Normalize lot: add current_quantity alias for components that don't know about the dual schema
      if (config.hasUnitOfMeasure) {
        aggregated[key].lots.push({ ...item, current_quantity: item.current_unit_quantity });
      } else if (config.hasUnitConfig) {
        aggregated[key].lots.push({ ...item, current_quantity: item.total_base_units });
      } else {
        aggregated[key].lots.push(item);
      }
    });
    
    return Object.values(aggregated);
  };
  
  /**
   * Refresh inventory data
   */
  const refresh = useCallback(() => {
    fetchInventoryItems();
  }, [fetchInventoryItems]);
  
  // Fetch data on mount and when dependencies change
  useEffect(() => {
    if (companyId && inventoryType) {
      fetchInventoryItems();
    }
  }, [companyId, inventoryType, fetchInventoryItems]);
  
  return {
    loading,
    error,
    inventoryData,
    aggregatedData,
    refresh
  };
};

/**
 * Hook for fetching single inventory item
 * @param {string} inventoryType - Type of inventory
 * @param {string} companyId - Company ID
 * @param {string} itemId - Item ID
 * @returns {object} Item data and loading state
 */
export const useInventoryItem = (inventoryType, companyId, itemId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [item, setItem] = useState(null);
  
  const config = getInventoryConfig(inventoryType);
  
  const fetchItem = useCallback(async () => {
    if (!itemId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from(config.tableName)
        .select('*')
        .eq('company_id', companyId)
        .eq('id', itemId)
        .single();
      
      if (fetchError) throw fetchError;
      
      setItem(data);
    } catch (err) {
      console.error('Error fetching inventory item:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inventoryType, companyId, itemId]);
  
  useEffect(() => {
    fetchItem();
  }, [fetchItem]);
  
  return {
    loading,
    error,
    item,
    refresh: fetchItem
  };
};

/**
 * Hook for fetching inventory transactions
 * @param {string} inventoryType - Type of inventory
 * @param {string} companyId - Company ID
 * @param {object} filters - Transaction filters
 * @returns {object} Transaction data and loading state
 */
export const useInventoryTransactions = (inventoryType, companyId, filters = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  const config = getInventoryConfig(inventoryType);
  
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from(config.transactionsTable)
        .select(`
          *,
          ${config.tableName} (
            id,
            name,
            sku
          ),
          locations (
            id,
            name
          ),
          suppliers (
            id,
            name
          )
        `)
        .eq('company_id', companyId);
      
      // Apply filters
      if (filters.transactionType && filters.transactionType !== 'all') {
        query = query.eq('transaction_type', filters.transactionType);
      }
      
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      
      const { data, error: fetchError } = await query.order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      setTransactions(data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inventoryType, companyId, filters]);
  
  useEffect(() => {
    if (companyId && inventoryType) {
      fetchTransactions();
    }
  }, [fetchTransactions]);
  
  return {
    loading,
    error,
    transactions,
    refresh: fetchTransactions
  };
};

/**
 * Hook for fetching all items (for dropdowns/selection)
 * @param {string} inventoryType - Type of inventory
 * @param {string} companyId - Company ID
 * @returns {object} Items data and loading state
 */
export const useInventoryItems = (inventoryType, companyId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  
  const config = getInventoryConfig(inventoryType);
  
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from(config.tableName)
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inventoryType, companyId]);
  
  useEffect(() => {
    if (companyId && inventoryType) {
      fetchItems();
    }
  }, [fetchItems]);
  
  return {
    loading,
    error,
    items,
    refresh: fetchItems
  };
};

/**
 * Hook for fetching locations
 * @param {string} companyId - Company ID
 * @returns {object} Locations data and loading state
 */
export const useLocations = (companyId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  
  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);
  
  useEffect(() => {
    if (companyId) {
      fetchLocations();
    }
  }, [fetchLocations]);
  
  return {
    loading,
    error,
    locations,
    refresh: fetchLocations
  };
};

/**
 * Hook for fetching suppliers
 * @param {string} companyId - Company ID
 * @returns {object} Suppliers data and loading state
 */
export const useSuppliers = (companyId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  
  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('suppliers')
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);
  
  useEffect(() => {
    if (companyId) {
      fetchSuppliers();
    }
  }, [fetchSuppliers]);
  
  return {
    loading,
    error,
    suppliers,
    refresh: fetchSuppliers
  };
};

export default {
  useInventoryData,
  useInventoryItem,
  useInventoryTransactions,
  useInventoryItems,
  useLocations,
  useSuppliers
};
